/**
 * Unified API Client
 * Central axios instance with interceptors for authentication and error handling
 */
import axios from "axios";
import { toast } from "react-toastify";
import { handle401, isAuthError } from "./handle401";

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor - Add auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("Token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle errors and token expiry
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Centralized 401 handling — clears localStorage, dispatches
    // LOGOUT_AUTH, and redirects to /#/login (HashRouter-correct).
    // Previously this used window.location.href = "/login", which
    // landed on an unmatched route under HashRouter and left the user
    // on a blank screen.

    if (isAuthError(error)) {
      const code    = error?.response?.data?.code;
      const message = error?.response?.data?.message;

      if (code === 'workspace_changed') {
        toast.error(message || 'Your workspace has been updated. Please login again.');
      }
      handle401();
      return Promise.reject(error);
    }

    // Normalize error response
    const normalizedError = {
      status: error?.response?.status || 500,
      message:
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong",
      data: error?.response?.data || null,
      originalError: error,
    };

    return Promise.reject(normalizedError);
  }
);

export default apiClient;

// Generic API helper functions
export const apiGet = (url, params, config = {}) =>
  apiClient.get(url, { params, ...config });

export const apiPost = (url, data, config = {}) =>
  apiClient.post(url, data, config);

export const apiPut = (url, data, config = {}) =>
  apiClient.put(url, data, config);

export const apiPatch = (url, data, config = {}) =>
  apiClient.patch(url, data, config);

export const apiDelete = (url, config = {}) => apiClient.delete(url, config);

// FormData POST helper
export const apiPostFormData = (url, formData, config = {}) =>
  apiClient.post(url, formData, {
    ...config,
    headers: {
      ...config.headers,
      "Content-Type": "multipart/form-data",
    },
  });

// Legacy callback-style wrappers for backward compatibility
export const apiGetWithCallback = async (url, successCallback, params, signal) => {
  try {
    const response = await apiClient.get(url, { params, signal });
    successCallback(response);
  } catch (error) {
    console.error("API Error:", error);
  }
};

export const apiPostWithCallback = async (url, successCallback, data) => {
  try {
    const response = await apiClient.post(url, data);
    successCallback(response.data);
  } catch (error) {
    console.error("API Error:", error);
  }
};

export const apiPostFormDataWithCallback = async (url, successCallback, formData) => {
  try {
    const response = await apiClient.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    successCallback(response.data);
  } catch (error) {
    console.error("API Error:", error);
  }
};

// Legacy wrappers with simplified naming
export const legacyGet = async (url, success, params, signal) => {
  try {
    const response = await apiClient.get(url, { params, signal });
    success(response);
  } catch (error) {
    console.error("API Error:", error);
  }
};

export const legacyPost = async (url, success, data) => {
  try {
    const response = await apiClient.post(url, data);
    success(response.data);
  } catch (error) {
    console.error("API Error:", error);
  }
};

export const legacyPostFormData = async (url, success, formData) => {
  try {
    const response = await apiClient.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    success(response.data);
  } catch (error) {
    console.error("API Error:", error);
  }
};
